import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Sparkles, Flame, MessageSquare, AlertCircle, Check, ChevronDown } from 'lucide-react';
import '../../styles/chats.css';

interface ChatListPanelProps {
  filteredList: any[];
  activeChatId: number | null;
  search: string;
  setSearch: (v: string) => void;
  filter: string;
  setFilter: (v: string) => void;
  unreadCount: number;
  channels: Record<string, any>;
  avatarColor: (name: string) => string;
  initials: (name: string) => string;
  onOpenChat: (id: number) => void;
  hasMoreRemote: boolean;
  onLoadMore: () => void;
}

export const ChatListPanel: React.FC<ChatListPanelProps> = ({
  filteredList,
  activeChatId,
  search,
  setSearch,
  filter,
  setFilter,
  unreadCount,
  channels,
  avatarColor,
  initials,
  onOpenChat,
  hasMoreRemote,
  onLoadMore
}) => {
  const [visibleCount, setVisibleCount] = useState(50);
  const [isChannelMenuOpen, setIsChannelMenuOpen] = useState(false);
  const channelMenuRef = useRef<HTMLDivElement>(null);
  const channelButtonRef = useRef<HTMLButtonElement>(null);
  const visibleList = useMemo(() => filteredList.slice(0, visibleCount), [filteredList, visibleCount]);
  const channelKeys = ['telegram', 'whatsapp', 'instagram', 'vk', 'avito', 'widget'];
  const selectedChannelKey = channelKeys.includes(filter) ? filter : null;
  const selectedChannel = selectedChannelKey ? channels[selectedChannelKey] : null;

  useEffect(() => {
    setVisibleCount(50);
  }, [search, filter]);

  useEffect(() => {
    if (!isChannelMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!channelMenuRef.current?.contains(event.target as Node)) {
        setIsChannelMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChannelMenuOpen(false);
        channelButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChannelMenuOpen]);

  const mainTabs = [
    { key: 'all', label: 'Все' },
    { key: 'unread', label: 'Новые', icon: <MessageSquare size={13} />, count: unreadCount },
    { key: 'hot', label: 'Горячие', icon: <Flame size={12} /> },
    { key: 'ai', label: 'ИИ', icon: <Sparkles size={12} /> },
  ];

  return (
    <div className="chat-list-panel">
      <div className="panel-head chat-panel-head">
        <h2>Чаты</h2>
        <span className="badge-count">{filteredList.length} диалогов</span>
      </div>

      <div className="chat-search">
        <div className="searchbox">
          <Search size={15} />
          <input
            placeholder="Поиск по имени или тексту..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="chat-filters-container">
        <div className="chat-smart-filters" aria-label="Быстрые фильтры чатов">
          {mainTabs.map(tab => (
            <button
              type="button"
              key={tab.key}
              className={`chat-smart-filter ${filter === tab.key ? 'active' : ''}`}
              aria-pressed={filter === tab.key}
              onClick={() => {
                setFilter(tab.key);
                setIsChannelMenuOpen(false);
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {'count' in tab && tab.count !== undefined && tab.count > 0 && <span className="chat-filter-count">{tab.count}</span>}
            </button>
          ))}
        </div>

        <div className="chat-channel-filter" ref={channelMenuRef}>
          <button
            ref={channelButtonRef}
            type="button"
            className={`chat-channel-trigger ${selectedChannel ? 'active' : ''}`}
            aria-haspopup="menu"
            aria-expanded={isChannelMenuOpen}
            aria-controls="chat-channel-menu"
            onClick={() => setIsChannelMenuOpen(open => !open)}
          >
            <span className="chat-channel-trigger-label">
              {selectedChannel ? (
                <span
                  className="chbadge"
                  style={{ background: selectedChannel.color }}
                  dangerouslySetInnerHTML={{ __html: selectedChannel.svg }}
                />
              ) : (
                <span className="channel-filter-mark" aria-hidden="true" />
              )}
              <span>{selectedChannel?.name || 'Все каналы'}</span>
            </span>
            <ChevronDown size={15} className={isChannelMenuOpen ? 'open' : ''} />
          </button>

          {isChannelMenuOpen && (
            <div id="chat-channel-menu" className="chat-channel-menu" role="menu" aria-label="Фильтр по каналу">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={!selectedChannelKey}
                className={!selectedChannelKey ? 'active' : ''}
                onClick={() => {
                  setFilter('all');
                  setIsChannelMenuOpen(false);
                }}
              >
                <span className="channel-filter-mark" aria-hidden="true" />
                <span>Все каналы</span>
                {!selectedChannelKey && <Check size={15} />}
              </button>
              {channelKeys.map(k => {
                const ch = channels[k];
                if (!ch) return null;
                return (
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={selectedChannelKey === k}
                    key={k}
                    className={selectedChannelKey === k ? 'active' : ''}
                    onClick={() => {
                      setFilter(k);
                      setIsChannelMenuOpen(false);
                    }}
                  >
                    <span
                      className="chbadge"
                      style={{ background: ch.color }}
                      dangerouslySetInnerHTML={{ __html: ch.svg }}
                    />
                    <span>{ch.name}</span>
                    {selectedChannelKey === k && <Check size={15} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Chat List Items */}
      <div className="chat-list">
        {filteredList.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            <AlertCircle size={28} style={{ margin: '0 auto 8px auto', opacity: 0.5 }} />
            Диалоги не найдены
          </div>
        ) : (
          <>
          {visibleList.map(c => {
            const chInfo = channels[c.channelType || c.channel?.type] || channels.widget;
            const rawTime = c.last_sent || c.lastTime || c.updatedAt || c.createdAt;
            let dTime = '';
            if (rawTime) {
              const d = new Date(rawTime);
              dTime = !isNaN(d.getTime())
                ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                : (typeof rawTime === 'string' ? rawTime : '');
            }

            return (
              <button
                type="button"
                key={c.id}
                className={`chat-item ${c.id === activeChatId ? 'active' : ''}`}
                onClick={() => onOpenChat(c.id)}
                aria-pressed={c.id === activeChatId}
              >
                <div className="avatar" style={{ '--av': avatarColor(c.contactName) } as any}>
                  {initials(c.contactName)}
                  <span
                    className="chbadge"
                    style={{ width: 17, height: 17, background: chInfo.color }}
                    dangerouslySetInnerHTML={{ __html: chInfo.svg }}
                  />
                </div>
                <div className="ci-body">
                  <div className="ci-top">
                    <span className="ci-name">
                      <span className="ci-name-text">{c.contactName}</span>
                      {c.temp === 'hot' && (
                        <span title="Горячий клиент" className="ci-hot-badge">
                          <Flame size={12} style={{ color: '#ef4444' }} />
                        </span>
                      )}
                    </span>
                    <span className="ci-time">{dTime}</span>
                  </div>
                  <div className="ci-bottom">
                    <span className="ci-last">
                      {c.aiEnabled && <span style={{ color: '#8b5cf6', fontWeight: 600, marginRight: '4px' }}>✦</span>}
                      {/* A preview that is a team note is labelled, so the list
                          never reads as if the note had been sent to the client. */}
                      {c.last_internal && <span className="ci-note-mark">Заметка:</span>}
                      {c.last_text || 'Нет сообщений'}
                    </span>
                    <div className="ci-badges">
                      {c.aiEnabled && (
                        <span
                          style={{
                            fontSize: '10px',
                            background: 'rgba(139, 92, 246, 0.12)',
                            color: '#7c3aed',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            fontWeight: 600
                          }}
                          title="ИИ агент подключен"
                        >
                          ИИ
                        </span>
                      )}
                      {c.unread > 0 && <span className="ci-unread">{c.unread}</span>}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {(visibleList.length < filteredList.length || hasMoreRemote) && (
            <div className="progressive-load compact">
              <span>Показано {visibleList.length} из {filteredList.length}</span>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setVisibleCount(count => count + 50); if (visibleList.length >= filteredList.length) onLoadMore(); }}>Показать ещё</button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};
